import React, { useMemo, useState } from "react";
import { Box, Chip, IconButton, Paper, Tooltip, useTheme } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { useIntl } from "react-intl";
import { tokens } from "../../../assets/theme";
import { hasAnyRole } from "../../../auth/authUi";
import { useAuth } from "../../../auth/AuthContext";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import { getAssetHierarchyPalette, getStatusChipSx } from "../../cmms/assetHierarchyStyles";
import { CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import messages from "../messages";

/*
 * The spare part types list.
 *
 * StorageLocationsDataGrid with a different noun, and deliberately so: two registers on the same
 * settings page that behave differently would be the inconsistency, not the duplication. No
 * GridToolbar and no remembered column order, width or visibility — three columns on a settings
 * tab have nothing to choose between, and those controls belong on the grids people live in.
 *
 * Not folded into one parameterised grid with the locations. The shared part is a name column, a
 * status chip and three icon buttons; the abstraction that covers both is bigger than the second
 * copy, and it would have to grow a seam the first time one of them needs a column the other
 * does not.
 *
 * Sorted by name, which is also the order the endpoint returns and the order the clustered index
 * on (CompanyKey, Name) already reads in.
 */
export default function SparePartTypesDataGrid({
  sparePartTypes,
  isLoading,
  showStatusColumn,
  onEdit,
  onDeactivate,
  onRestore,
  emptyState,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const colors = tokens(theme.palette.mode);
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const { roleIds } = useAuth();
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;

  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);

  const canManage = hasAnyRole(roleIds, CMMS_MANAGER_ROLES);

  const columns = useMemo(() => {
    const cols = [
      {
        field: "name",
        headerName: intl.formatMessage(messages.sparePartTypeColumnName),
        flex: 1,
        minWidth: 200,
      },
    ];

    // Only while inactive rows can be on screen, the same call the register makes: a status column
    // on a list that is all one status is a column of identical chips.
    if (showStatusColumn) {
      cols.push({
        field: "isActive",
        headerName: intl.formatMessage(messages.columnActive),
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Chip
            size="small"
            label={intl.formatMessage(params.row.isActive ? messages.statusActive : messages.statusInactive)}
            sx={getStatusChipSx(cmmsColors, params.row.isActive)}
          />
        ),
      });
    }

    cols.push({
      field: "actions",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box onClick={(event) => event.stopPropagation()}>
          {canManage &&
            <Tooltip title={intl.formatMessage(messages.sparePartTypeActionEdit)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.sparePartTypeActionEdit)}
                onClick={() => onEdit?.(params.row)}
                sx={{ backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
          {/*
            * Deactivate and its opposite share the slot, the same as the spare parts register. An
            * inactive row is only on screen with "Show inactive" on, and the one thing left to do
            * to it there is bring it back.
            */}
          {canManage && params.row.isActive &&
            <Tooltip title={intl.formatMessage(messages.sparePartTypeActionDeactivate)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.sparePartTypeActionDeactivate)}
                onClick={() => onDeactivate?.(params.row)}
                sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <BlockIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
          {canManage && !params.row.isActive &&
            <Tooltip title={intl.formatMessage(messages.sparePartTypeActionRestore)}>
              <IconButton
                size="small"
                aria-label={intl.formatMessage(messages.sparePartTypeActionRestore)}
                onClick={() => onRestore?.(params.row)}
                sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
              >
                <RestoreFromTrashIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
        </Box>
      ),
    });

    return cols;
  }, [intl, colors, cmmsColors, canManage, showStatusColumn, onEdit, onDeactivate, onRestore]);

  const gridSlots = useMemo(
    () => ({
      loadingOverlay: () => (
        <Box sx={{ px: 2, pt: 1.5, width: "100%" }}>
          <CmmsListSkeleton rows={6} height={36} />
        </Box>
      ),
      // Only when the caller supplied one — an overlay rendering `undefined` would cover the grid
      // with nothing.
      ...(emptyState ? {
        noRowsOverlay: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            {emptyState}
          </Box>
        ),
      } : {}),
    }),
    [emptyState],
  );

  return (
    <Paper sx={{ position: "relative", borderRadius: "4px" }}>
      <Box
        height="60vh"
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
        }}
      >
        <DataGridPro
          loading={isLoading}
          slots={gridSlots}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          rows={sparePartTypes || []}
          getRowId={(row) => row.id}
          columns={columns}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
